/**
 * tourSteps/index.js
 * Central registry of all tour step definitions and safe target resolver.
 */

import { projectsListSteps } from './projectsListSteps';
import { taskListSteps } from './taskListSteps';
import { kanbanSteps } from './kanbanSteps';
import { timelineSteps } from './timelineSteps';
import { sprintSteps } from './sprintSteps';
import { taskDrawerSteps } from './taskDrawerSteps';
import { milestoneSteps } from './milestoneSteps';
import { timeTrackingSteps } from './timeTrackingSteps';
import { projectFilesSteps } from './projectFilesSteps';
import { inboxSteps } from './inboxSteps';
import { myWorkSteps } from './myWorkSteps';
import { createTaskSteps } from './createTaskSteps';

export const TOUR_DEFINITIONS = {
    // ── Creation Drawer ──
    createTask: {
        id: 'createTask',
        title: 'Create Task or Milestone',
        description: 'Step-by-step guide to creating new tasks or milestones, required fields, and delivery settings.',
        steps: createTaskSteps,
    },

    // ── Workspace Sub-Views ──
    taskList: {
        id: 'taskList',
        title: 'Task List & Column Guide',
        description: 'Detailed breakdown of every table column, work item types, stages, and effort tracking.',
        steps: taskListSteps,
    },
    kanban: {
        id: 'kanban',
        title: 'Kanban Board',
        description: 'Agile workflow lanes, quick column task creation, drag & drop, and card details.',
        steps: kanbanSteps,
    },
    timeline: {
        id: 'timeline',
        title: 'Timeline & Gantt Roadmap',
        description: 'Continuous schedule grid, dependency lines, risk alerts, and timescale zoom.',
        steps: timelineSteps,
    },
    sprint: {
        id: 'sprint',
        title: 'Sprint Management',
        description: 'Sprint cadence, health KPIs, risk predictions, close-sprint and reports.',
        steps: sprintSteps,
    },
    taskBoard: {
        id: 'taskBoard',
        title: 'Task Workspace',
        description: 'Detailed breakdown of the table directory, status columns, and effort tracking.',
        steps: taskListSteps,
    },

    // ── Primary Modules ──
    projectsList: {
        id: 'projectsList',
        title: 'Project Dashboard',
        description: 'Explore project metrics, filter directory, CRE requests, and project creation.',
        steps: projectsListSteps,
    },
    taskDrawer: {
        id: 'taskDrawer',
        title: 'Task & Milestone Drawer',
        description: 'Explore Tasks vs Milestones, Child Tasks, Deliverables, AI summaries, and timers.',
        steps: taskDrawerSteps,
    },
    milestones: {
        id: 'milestones',
        title: 'Milestones & Timelines',
        description: 'Track major phase deliverables, task links, and completion rules.',
        steps: milestoneSteps,
    },
    projectFiles: {
        id: 'projectFiles',
        title: 'Project Files',
        description: 'Browse the 5-folder architecture, file uploads, AI organization, and asset preview.',
        steps: projectFilesSteps,
    },
    inbox: {
        id: 'inbox',
        title: 'Action Inbox',
        description: 'Review communications, AI digests, urgent action items, and activity streams.',
        steps: inboxSteps,
    },
    myWork: {
        id: 'myWork',
        title: 'My Work Hub',
        description: 'Manage personal assigned tasks, AI work briefings, and capacity workload.',
        steps: myWorkSteps,
    },
    timeTracking: {
        id: 'timeTracking',
        title: 'Time Tracking & Logs',
        description: 'Learn how to use active timers, effort tracking, and workload charts.',
        steps: timeTrackingSteps,
    },
};

/**
 * Creates safe target getter for Ant Design Tour.
 * Gracefully returns null if selector is not currently mounted in the DOM.
 */
export function getSafeTarget(selector, fallbackSelector = null) {
    return () => {
        if (!selector) return null;
        try {
            const el = document.querySelector(selector);
            if (el) return el;
            if (fallbackSelector) {
                const fallbackEl = document.querySelector(fallbackSelector);
                if (fallbackEl) return fallbackEl;
            }
        } catch (e) {
            console.warn(`[Tour] Error querying target: ${selector}`, e);
        }
        return null;
    };
}
